// flowchart TD
//   A[Start: input object or array] --> B[Normalize to array of roots]
//   B --> C[For each root -> traverse(node)]
//   C --> D[Pick module & value from node]
//   D --> E{Find child arrays that contain objects?}
//   E -- Yes --> F[For each child array: for each item -> traverse(item)]
//   F --> G[Collect all returned child nodes into data array]
//   E -- No --> G
//   G --> H[Return {module, value, data}]
//   H --> I[Append to result list]
//   I --> J{More roots?}
//   J -- Yes --> C
//   J -- No --> K[Finish: output array of tree nodes]

/**
 * convertToTreeFormat
 * Input: single object or array of objects (roots)
 * Output: array of tree nodes: [{ module, value, data: [childNodes...] }, ...]
 */

export function convertToTreeFormat(input) {
  const roots = Array.isArray(input) ? input : [input];

  const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

  function pickModuleAndValue(obj) {
    // Only consider string-valued entries
    const entries = Object.entries(obj).filter(([k, v]) => typeof v === "string" && v !== "");

    // Regexes to detect keys (support snake_case and camelCase)
    const reName = /^(name|title|label)$/i;
    const reStoreId = /^(store(_|-)?id|storeId)$/i;
    const reIdLike = /(^id$|_id$|Id$)$/i; // fallback for other id forms
    const reValue = /^value$/i;
    const reCode = /^code$/i;

    // Pick module: prefer name/title/label, then code, then first string entry
    const moduleEntry =
      entries.find(([k]) => reName.test(k)) ||
      entries.find(([k]) => reCode.test(k)) ||
      entries[0] ||
      null;

    let moduleKey = moduleEntry ? moduleEntry[0] : undefined;
    let moduleVal = moduleEntry ? moduleEntry[1] : undefined;

    // Pick value: priority -> store_id/storeId -> any id-like -> value -> code
    let valueEntry =
      entries.find(([k]) => reStoreId.test(k)) ||
      entries.find(([k]) => reIdLike.test(k)) ||
      entries.find(([k]) => reValue.test(k)) ||
      entries.find(([k]) => reCode.test(k)) ||
      null;

    let valueKey = valueEntry ? valueEntry[0] : undefined;
    let valueVal = valueEntry ? valueEntry[1] : undefined;

    // If value ends up equal to module, try to find a different string
    if (valueVal === moduleVal) {
      const different = entries.find(([k, v]) => v !== moduleVal);
      if (different) {
        valueKey = different[0];
        valueVal = different[1];
      }
    }

    // Final fallbacks
    if (!moduleVal && entries.length) {
      moduleKey = entries[0][0];
      moduleVal = entries[0][1];
    }
    if (!valueVal) {
      // prefer something different from moduleVal if possible
      const different = entries.find(([k, v]) => v !== moduleVal);
      if (different) {
        valueKey = different[0];
        valueVal = different[1];
      } else {
        valueKey = moduleKey;
        valueVal = moduleVal !== undefined ? moduleVal : JSON.stringify(obj).slice(0, 30);
      }
    }

    return {
      module: moduleVal,
      value: valueVal,
      // optional debug keys if you want to know which original keys were used
      // moduleKey,
      // valueKey,
    };
  }

  function traverse(obj) {
    // Convert current node
    const { module, value } = pickModuleAndValue(obj);

    // Find child arrays whose elements include plain objects
    const childArrays = Object.values(obj).filter(
      (v) => Array.isArray(v) && v.some((el) => isPlainObject(el))
    );

    // Collect children from each such array
    const children = [];
    for (const arr of childArrays) {
      for (const item of arr) {
        if (isPlainObject(item)) {
          children.push(traverse(item));
        }
      }
    }

    // Defensive: also traverse any plain-object properties (not arrays)
    const plainChildProps = Object.values(obj).filter(isPlainObject);
    for (const c of plainChildProps) {
      children.push(traverse(c));
    }

    return {
      module,
      value,
      data: children,
    };
  }

  // Build the tree for each root
  return roots.flatMap((r) => (isPlainObject(r) ? [traverse(r)] : []));
}